import React, { useEffect, useState } from "react";
import { formatPrice } from "../../utils/formatPrice";
import { trackEvent } from "../../utils/analytics";
import { Box, Button, Grid, Typography, Switch, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import dayjs from "dayjs";
import SailRangePicker from "./SailRangePicker";
import { useSelector } from "react-redux";
import ReservationModal from "../ReservationModal";
import coverPhoto from "../../assets/images/landscape-cover.jpg";
import moment from "moment";

interface BookingWidgetProps {
  id: string | number;
  discount: any;
  price: any; // per day price
  startPrice: any;
  skipperData: {
    price: number;
    obligatory: boolean;
    name: string;
    unit: string;
  } | null;
  formData: {
    checkIn: string;
    checkOut: string;
    name: string;
    surname: string;
    email: string;
    telephone: string;
    notes: string;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  isUnavailable?: boolean;
  isOnOption?: boolean;
  optionExpiry?: string;
  charteredWeeks?: number;
}

const BookingWidget: React.FC<BookingWidgetProps> = ({
  id,
  price,
  discount,
  formData,
  startPrice,
  setFormData,
  skipperData,
  isUnavailable = false,
  isOnOption = false,
  optionExpiry,
  charteredWeeks,
}) => {
  const { yacht } = useSelector((state: any) => state.yacht);
  const [skipper, setSkipper] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [extrasOpen, setExtrasOpen] = useState(false);

  const getTotalDays = () => {
    const checkInDate = dayjs(formData.checkIn);
    const checkOutDate = dayjs(formData.checkOut);
    if (checkInDate.isValid() && checkOutDate.isValid()) {
      const days = checkOutDate.diff(checkInDate, "day");
      return days > 0 ? days : 0;
    }
    return 0;
  };

  const totalDays = getTotalDays();

  const unitNorm = (skipperData?.unit || "").toLowerCase().replace(/_/g, " ");
  const skipperPrice = skipperData?.price
    ? (unitNorm === "per day" || unitNorm === "per night")
      ? skipperData.price * totalDays
      : skipperData.price
    : 0;

  const finalPrice = price != null
    ? (skipperData?.obligatory ? price : skipper ? price + skipperPrice : price)
    : null;

  const toggleSkipper = () => {
    if (skipperData?.obligatory) return;
    setSkipper((prev) => !prev);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const generateText = (status: number | string, expDate?: string) => {
    const s = status === "B" ? "B" : Number(status);
    if (s === 0) {
      return {
        title: "This boat is in high demand!",
        description: "5+ people are also thinking of booking this boat.",
      };
    } else if (s === 1) {
      return {
        title: "Reservation",
        description: "Already reserved, not available.",
      };
    } else if (s === 2) {
      return {
        title: `Option opened for another client until ${moment(expDate).format("DD/M/YYYY")}`,
        description: "Send us your request now to add yourself to the queue.",
      };
    } else if (s === 3) {
      return {
        title: "Option in expiration",
        description: "Option expires soon.",
      };
    } else if (s === 4) {
      return {
        title: "Service",
        description: "The boat is currently in service.",
      };
    } else if (s === "B") {
      return {
        title: "Sleepaboard",
        description:
          "You can sleep on the boat the night before the departure date.",
      };
    } else {
      return {};
    }
  };

  const status = yacht?.status;
  const expDate = yacht?.expirationDate ? yacht?.expirationDate : new Date();
  const statusText = generateText(status, expDate);

  const reserveBoat = async () => {
    trackEvent('reservation_clicked', {
      yacht_id: String(id || ''),
      yacht_name: yacht?.name || '',
      yacht_model: yacht?.model || '',
      price: price || '',
    });
    handleOpenModal();
    // try {
    //   const data = {
    //     date_from: formData.checkIn,
    //     date_to: formData.checkOut,
    //     yacht_id: id,
    //     customer_name: formData.name,
    //     customer_email: formData.email,
    //   };
    //   const response = await reserveThisBoat(data);
    // } catch (error) {
    //   console.error("Error during reservation:", error);
    // }
  };

  useEffect(() => {
    if (skipperData?.obligatory) {
      setSkipper(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        sx={{
          mt: { xs: -4, sm: -4, md: 0 },
          p: 3,
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          backgroundColor: {
            xs: "#f1f1f1",
            md: "#fff",
          },
        }}
      >
        <Typography variant="h6" gutterBottom>
          Your selection price
        </Typography>

        <Box
          sx={{
            mb: 2,
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "6px 12px 8px",
            background: "#ffffff",
          }}
        >
          <Grid container sx={{ mb: "2px" }}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#484a4b", textTransform: "uppercase" }}>
                Check-in
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#484a4b", textTransform: "uppercase", pl: 2 }}>
                Check-out
              </Typography>
            </Grid>
          </Grid>
          <SailRangePicker
            startDate={formData.checkIn ? new Date(formData.checkIn) : null}
            endDate={formData.checkOut ? new Date(formData.checkOut) : null}
            onChange={(start, end) =>
              setFormData((prev: any) => ({
                ...prev,
                checkIn: start.toISOString().split("T")[0],
                checkOut: end.toISOString().split("T")[0],
              }))
            }
            placeholder={["Check-in", "Check-out"]}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Price :</Typography>
          <Typography>{formatPrice(startPrice != null && startPrice > 0 ? startPrice : null)}</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Number of Days:</Typography>
          <Typography>{totalDays}</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Discount:</Typography>
          <Typography>{discount}%</Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography>Skipper:</Typography>
          <Switch
            checked={skipper}
            onChange={toggleSkipper}
            disabled={skipperData == null}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6">Total:</Typography>
          <Typography variant="h6">{formatPrice(finalPrice)}</Typography>
        </Box>

        {yacht?.obligatoryExtras?.length > 0 ? (
          <Typography
            variant="caption"
            onClick={() => setExtrasOpen(true)}
            sx={{ color: "gray", cursor: "pointer", textDecoration: "underline dotted" }}
          >
            Excluding mandatory services ℹ️
          </Typography>
        ) : (
          <Typography variant="caption" color="gray">
            Excluding mandatory services
          </Typography>
        )}

        <Dialog open={extrasOpen} onClose={() => setExtrasOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Mandatory Services</DialogTitle>
          <DialogContent>
            <List dense>
              {yacht?.obligatoryExtras?.map((extra: any) => (
                <ListItem key={extra.id} sx={{ px: 0 }}>
                  <ListItemText primary={extra.name} />
                  <Typography variant="body2">{formatPrice(extra.price, extra.currency || '€')}</Typography>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography fontWeight="bold">Total</Typography>
              <Typography fontWeight="bold">
                {formatPrice(yacht?.obligatoryExtras?.reduce((sum: number, e: any) => sum + (e.price || 0), 0))}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExtrasOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Button
          variant="contained"
          color="error"
          fullWidth
          disabled={isUnavailable || (formData?.checkIn && formData?.checkOut ? false : true)}
          onClick={reserveBoat}
          sx={{
            mt: 2,
            pt: 1,
            borderRadius: "30px",
            fontSize: "13px",
          }}
        >
          Make a Reservation
        </Button>

        {/* Availability status section */}
        <Box sx={{ mt: 2 }}>
          {isUnavailable ? (
            <Box
              sx={{
                p: 1.5,
                bgcolor: "#fdecea",
                borderRadius: 1,
                border: "1px solid #f5c6cb",
              }}
            >
              <Typography color="error" fontSize="13px" fontWeight="bold">
                Not available for the selected dates.
              </Typography>
              <Typography color="error" fontSize="13px" mt={0.5}>
                Please choose another week from the availability calendar below.
              </Typography>
            </Box>
          ) : isOnOption ? (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <WatchLaterIcon sx={{ color: "#f57c00", fontSize: "1.5rem", flexShrink: 0, mt: "2px" }} />
              <Box>
                <Typography fontSize="12px" fontWeight="bold">
                  {(Number(status) === 2 || Number(status) === 3) ? statusText?.title : "This boat is currently under option with another client"}
                </Typography>
                <Typography fontSize="12px">
                  {(Number(status) === 2 || Number(status) === 3) ? statusText?.description : "Send us your request now to add yourself to the queue."}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
              <CheckCircleIcon sx={{ color: "green", fontSize: "1.5rem", flexShrink: 0, mt: "2px" }} />
              <Box>
                <Typography fontSize="12px" fontWeight="bold">Confirmed Availability</Typography>
                <Typography fontSize="12px">Updated availability and pricing. You can book immediately.</Typography>
                <Typography fontSize="12px">Updated: now</Typography>
              </Box>
            </Box>
          )}
          {!isUnavailable && charteredWeeks !== undefined && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
              {charteredWeeks >= 11
                ? <LocalFireDepartmentIcon sx={{ color: "#e65100", fontSize: "1.5rem", flexShrink: 0, mt: "2px" }} />
                : <EventAvailableIcon sx={{ color: "#2e7d32", fontSize: "1.5rem", flexShrink: 0, mt: "2px" }} />
              }
              <Box>
                <Typography fontSize="12px" fontWeight="bold">
                  {charteredWeeks >= 11 ? "Popular yacht — high demand" : "Good availability this season"}
                </Typography>
                <Typography fontSize="12px">
                  {charteredWeeks >= 11 ? (
                    <><strong>{charteredWeeks} weeks</strong> already booked this year.</>
                  ) : (
                    <>Only <strong>{charteredWeeks} week{charteredWeeks !== 1 ? "s" : ""}</strong> booked so far this year.</>
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <ReservationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        images={
          yacht?.yacht?.images?.length > 0 ? yacht?.yacht?.images : coverPhoto
        }
        name={`${yacht?.yacht?.model}
        ${yacht?.yacht?.cabins_note && `- ${yacht?.yacht?.cabins_note} Cab`} -
        ${yacht?.yacht?.home_base_name} `}
        kind={yacht?.yacht?.kind}
        price={finalPrice || 0}
        formData={formData}
        setFormData={setFormData}
        yachtDetails={yacht}
        id={yacht?.yachtId}
        startPrice={startPrice || 0}
        skipper={skipper}
        startBaseId={yacht?.startBaseId}
        endBaseId={yacht?.endBaseId}
        skipperData={skipperData}
        status={isOnOption ? 2 : 0}
        optionExpiry={optionExpiry ?? yacht?.expirationDate}
      />
    </>
  );
};

export default BookingWidget;
